import { useEffect, useRef, useCallback, useState } from "react";
import { useApi } from "./useApi";
import { saveService } from "../services/save";
import { SavePayload, SaveResponse } from "../types/save.types";

const AUTOSAVE_DELAY_MS = 1500;

export type SaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Salva `payload` em /chat/save 1,5s depois da última mudança em `watchValues`.
 * Sem toast de sucesso: a tela mostra `<SaveStatus status savedAt />`. Erro vira
 * toast com "Tentar novamente" (useApi). Uma mudança ainda não salva é enviada na
 * hora ao sair da tela, ao trocar de aba ou ao fechar a página.
 * ponytail: o fetch do pagehide pode ser cortado pelo navegador; usar `keepalive`
 * no saveService se aparecer pedido perdido ao fechar a aba.
 */
export const useAutoSave = (payload: SavePayload = {}, watchValues: unknown[] = []) => {
  const { request, apiFetch } = useApi<SaveResponse>();
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const payloadRef = useRef<SavePayload>(payload);
  const timer = useRef<number>();
  const pending = useRef(false);
  const isMounted = useRef(false);

  useEffect(() => {
    payloadRef.current = payload;
  });

  const autoSave = useCallback(async (): Promise<boolean> => {
    window.clearTimeout(timer.current);
    pending.current = false;
    // Sem timestamp não há pedido no backend (ex.: resetDraft() antes de sair da tela).
    if (!payloadRef.current.timestamp) return true;
    setStatus("saving");
    const result = await request(() => saveService.post(apiFetch, payloadRef.current));
    const ok = result?.status === "success";
    setStatus(ok ? "saved" : "error");
    if (ok) setSavedAt(new Date());
    return ok;
  }, [request, apiFetch]);

  const autoSaveRef = useRef(autoSave);
  autoSaveRef.current = autoSave;

  useEffect(() => {
    if (watchValues.length === 0) return;
    // Pula a primeira execução (valores iniciais não são mudança do médico).
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    pending.current = true;
    timer.current = window.setTimeout(() => autoSaveRef.current(), AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer.current);
    // Os valores observados são as próprias dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, watchValues);

  useEffect(() => {
    const flush = () => { if (pending.current) autoSaveRef.current(); };
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      flush(); // saiu da tela com mudança pendente
      // StrictMode monta duas vezes: sem isto a segunda montagem salvaria à toa.
      isMounted.current = false;
    };
  }, []);

  return { autoSave, status, savedAt };
};
